import * as Sentry from '@sentry/react';
import { makeFetchTransport, makeMultiplexedTransport } from '@sentry/browser';
import { config } from 'config';

const enabled =
  !!config.sentryDsn &&
  ![config.env, config.robinEnv].some(
    (env) => env === 'development' || env === 'test'
  );

// Configure multiplexed transport to route errors to different Sentry projects
// based on the 'dsn' tag. Events without a tag go to the default DSN.
const transport = makeMultiplexedTransport(
  makeFetchTransport,
  ({ getEvent }) => {
    const event = getEvent(['event', 'transaction', 'replay_event']);

    if (!event) {
      return [];
    }

    // Route to relevant DSN based on the tag
    if (event.tags?.dsn) {
      return [event.tags.dsn as string];
    }

    // Default: route to shell project (empty array means use default DSN)
    return [];
  }
);

Sentry.init({
  enabled: enabled,
  environment: config.robinEnv,
  dsn: config.sentryDsn,
  integrations: [
    Sentry.browserTracingIntegration(),
    // Replay records the entire document so micro-frontends will be visible.
    // Text and media are masked and blocked by default.
    Sentry.replayIntegration(),
  ],
  tracesSampleRate: 1.0,
  // Only capture replays on errors, small quota :(
  replaysSessionSampleRate: 0,
  replaysOnErrorSampleRate: 1.0,
  normalizeDepth: 10,
  transport: transport,
  ignoreErrors: [
    // https://robin.sentry.io/issues/OFFICE-UI-B
    // https://robinpowered.slack.com/archives/CDFLQ7KN3/p1786118224505549
    'This account has been disabled. Please reconnect your account.',
    // Backend faults the client can't act on: a subgraph being unreachable or
    // erroring, one rejecting requests while it restarts, and the gateway
    // failing to resolve the client IP. Filtered here because every app shares
    // this Sentry client.
    /HTTP fetch failed from '/,
    /Cannot execute GraphQL operations (while the server is stopping|after the server has stopped)/,
    /IP address must be public, but .* is in private range/,
    // Requests cancelled by the browser or by us (navigation away, unmount,
    // AbortController). Chrome, Firefox, and Safari each word this differently.
    'The user aborted a request',
    'AbortError: The operation was aborted',
    'signal is aborted without reason',
    'Fetch is aborted',
    // The user navigated away or lost connectivity mid-request. These are
    // network conditions, not bugs we can act on.
    'Failed to fetch',
    'NetworkError when attempting to fetch resource',
    'Load failed',
    // A deploy happened while the tab was open and a lazily-loaded chunk from
    // the previous build is gone. A refresh resolves it.
    /Loading (CSS )?chunk [^\s]+ failed/,
    'ChunkLoadError',
    // Benign browser warning; the observed layout still settles correctly.
    'ResizeObserver loop limit exceeded',
    'ResizeObserver loop completed with undelivered notifications',
  ],
});

export { Sentry };
